import React from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
    formatNumberWithCommas,
    roundOff,
} from "repoV2/features/Common/utils/numbers";
import { pluralise } from "repoV2/features/Common/utils/strings";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import { classNames } from "repoV2/utils/common/render/classNames";
import commonStyles from "repoV2/features/CheckoutPages/modules/CheckoutAccordion/CheckoutAccordion.module.scss";
import CheckoutAccordion from "../../../CheckoutAccordion/CheckoutAccordion";
import { usePaymentSummarySection } from "../../utils/usePaymentSummarySection";
import { useStandardPaymentSummary } from "./StandardPaymentSummary.utils";
import { BillItemShimmer } from "../BillItemShimmer/BillItemShimmer";
import { single_unit_value } from "../../PaymentSummarySection.constants";
import styles from "../../PaymentSummarySection.module.scss";

const StandardPaymentSummary = () => {
    const {
        commonPropsOfPaymentSummaryTypes: {
            baseListingTitle,
            creatorTaxPercentage,
            exlyIHFPercentage,
            exlyGSTPercentage,

            listingPriceMultiplier,
            customersCount,

            basePrice,
            basePriceWOffers,
            basePriceWMultiplier,
            basePriceWOffersNMultiplier,

            exlyIHFAmount,
            exlyTaxAmount,

            totalAmount,

            discountOffType,
            discountIncludesGst,
            discountAmount,
            currencySymbol,
            currencyLocale,

            listingTypeSpecificUnitLabel,

            paymentSummaryHeading,

            orderBumpsArray,
            totalOrderBumpsValueWithTaxes,
            paymentSummarySubHeading,

            shippingValueWithTaxes,

            showShippingCharges,
        },
        isFetched,
    } = usePaymentSummarySection();

    // business logic
    const {
        basePriceCreatorTaxAmount,
        basePriceWOffersCreatorTaxAmount,
        basePriceWOffersNMultiplierCreatorTaxAmount,
        representationalDiscount,
    } = useStandardPaymentSummary({
        totalAmount,

        basePrice,
        creatorTaxPercentage,
        exlyIHFPercentage,
        exlyGSTPercentage,

        basePriceWOffers,

        basePriceWMultiplier,

        basePriceWOffersNMultiplier,
        totalOrderBumpsValueWithTaxes,

        discountOffType,
        discountAmount,
        discountIncludesGst,
    });

    // render logic
    const [isExpanded, setIsExpanded] = React.useState(false); // keep the accordian closed initially

    const strikeOffUnitPrice =
        basePrice + basePriceCreatorTaxAmount >
        basePriceWOffers + basePriceWOffersCreatorTaxAmount;

    return (
        <CheckoutAccordion
            showExpandIcon
            title={
                !isExpanded ? (
                    <div className={styles.collapsedTextContainer}>
                        <div className={styles.collapsedText}>
                            {paymentSummaryHeading}:&nbsp;
                            <BillItemShimmer
                                fetchedJSX={
                                    <>
                                        {currencySymbol}
                                        {formatNumberWithCommas({
                                            num: roundOff(totalAmount),
                                            locale: currencyLocale,
                                        })}
                                    </>
                                }
                                isFetched={isFetched}
                                overrideClassName={styles.headerShimmer}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={styles.collapsedTextContainer}>
                        <div className={styles.subHeadingContainer}>
                            <div className={styles.collapsedText}>
                                Payment Summary
                            </div>
                            <div className={styles.subHeadingText}>
                                {paymentSummarySubHeading}
                            </div>
                        </div>
                    </div>
                )
            }
            className={commonStyles.Wrapper}
            expandIcon={
                !isExpanded ? (
                    <div className={styles.viewSummaryBtn}>
                        Details&nbsp;
                        <KeyboardArrowDownRoundedIcon fontSize="small" />
                    </div>
                ) : (
                    <div
                        className={classNames(
                            styles.viewSummaryBtn,
                            isExpanded && styles.expandIconStyle
                        )}
                    >
                        See less&nbsp;
                        <KeyboardArrowUpRoundedIcon fontSize="small" />
                    </div>
                )
            }
            classes={{
                root: commonStyles.formGroupRoot,
                disabled: commonStyles.formGroupDisabled,
            }}
            headerClasses={{
                root: classNames(
                    commonStyles.headerRoot,
                    styles.cursorStyle,
                    isExpanded && styles.extraHeaderStyle
                ),
                content: commonStyles.headerContent,
                disabled: commonStyles.formGroupDisabled,
                expanded: commonStyles.formGroupExpanded,
                expandIconWrapper: styles.noRotateStyle,
            }}
            detailsClasses={{ root: styles.detailsRoot }}
            onTitleClick={() => setIsExpanded(!isExpanded)}
            unmountOnExit
        >
            <div className={styles.billItem}>
                <div className={styles.billItemLabel}>Customer count</div>
                <div className={styles.billItemvalue}>
                    <CloseRoundedIcon
                        className={classNames(
                            styles.crossIconStyle,
                            styles.valueCrossIconStyle
                        )}
                    />
                    &nbsp;
                    <BillItemShimmer
                        fetchedJSX={customersCount}
                        isFetched={isFetched}
                    />
                </div>
            </div>

            <div className={styles.billItem}>
                <div>
                    <div className={styles.billItemLabel}>
                        {orderBumpsArray.length ? (
                            <div className={styles.billItemLabel}>
                                <div>{single_unit_value}</div>&nbsp;
                                <CloseRoundedIcon
                                    className={styles.crossIconStyle}
                                />
                                &nbsp;
                                <div>{baseListingTitle}</div>
                            </div>
                        ) : (
                            <div className={styles.billItemLabel}>
                                Unit price
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.billItemvalue}>
                    <BillItemShimmer
                        fetchedJSX={
                            <>
                                {strikeOffUnitPrice ? (
                                    <del>
                                        {currencySymbol}
                                        {formatNumberWithCommas({
                                            num: roundOff(
                                                basePrice +
                                                    basePriceCreatorTaxAmount
                                            ),
                                            locale: currencyLocale,
                                        })}
                                    </del>
                                ) : null}
                                &nbsp;
                                {currencySymbol}
                                {formatNumberWithCommas({
                                    num: roundOff(
                                        basePriceWOffers +
                                            basePriceWOffersCreatorTaxAmount
                                    ),
                                    locale: currencyLocale,
                                })}
                            </>
                        }
                        isFetched={isFetched}
                    />
                </div>
            </div>
            {listingPriceMultiplier > 1 ? (
                <div className={styles.billItem}>
                    <div className={styles.billItemLabel}>
                        {pluralise(
                            listingTypeSpecificUnitLabel,
                            listingPriceMultiplier
                        )}
                    </div>
                    <div className={styles.billItemvalue}>
                        <CloseRoundedIcon className={styles.crossIconStyle} />
                        &nbsp;
                        <BillItemShimmer
                            fetchedJSX={listingPriceMultiplier}
                            isFetched={isFetched}
                        />
                    </div>
                </div>
            ) : null}

            {orderBumpsArray?.length
                ? orderBumpsArray.map(orderBump => (
                      <div key={orderBump.listingUuid}>
                          <div className={styles.billItem}>
                              <div className={styles.billItemLabel}>
                                  <div>{orderBump.quantity}</div>&nbsp;
                                  <CloseRoundedIcon
                                      className={styles.crossIconStyle}
                                  />
                                  &nbsp;
                                  {pluralise(
                                      orderBump.title,
                                      orderBump.quantity
                                  )}
                              </div>
                              <div className={styles.billItemvalue}>
                                  <BillItemShimmer
                                      fetchedJSX={
                                          <>
                                              {currencySymbol}
                                              {formatNumberWithCommas({
                                                  num: roundOff(
                                                      orderBump.finalPriceWithTaxes
                                                  ),
                                                  locale: currencyLocale,
                                              })}
                                          </>
                                      }
                                      isFetched={isFetched}
                                  />
                              </div>
                          </div>
                          {orderBump.shippingPriceWithTaxes ? (
                              <div className={styles.billItem}>
                                  <div className={styles.billItemLabel}>
                                      Shipping Charges
                                  </div>
                                  <div className={styles.billItemvalue}>
                                      <BillItemShimmer
                                          fetchedJSX={
                                              <>
                                                  {currencySymbol}
                                                  {formatNumberWithCommas({
                                                      num: roundOff(
                                                          orderBump.shippingPriceWithTaxes
                                                      ),
                                                      locale: currencyLocale,
                                                  })}
                                              </>
                                          }
                                          isFetched={isFetched}
                                      />
                                  </div>
                              </div>
                          ) : null}
                      </div>
                  ))
                : null}
            {showShippingCharges ? (
                <div className={styles.billItem}>
                    <div className={styles.billItemLabel}>Shipping Charges</div>
                    <div className={styles.billItemvalue}>
                        <BillItemShimmer
                            fetchedJSX={
                                <>
                                    {currencySymbol}
                                    {formatNumberWithCommas({
                                        num: roundOff(shippingValueWithTaxes),
                                        locale: currencyLocale,
                                    })}
                                </>
                            }
                            isFetched={isFetched}
                        />
                    </div>
                </div>
            ) : null}
            <div className={styles.billItem}>
                <div>
                    <div className={styles.billItemLabel}>Subtotal</div>
                    <div
                        className={classNames(
                            styles.billItemSubLabel,
                            styles.billItemSubLabelColor
                        )}
                    >
                        (Inclusive of taxes)
                    </div>
                </div>
                <div className={styles.billItemvalue}>
                    <BillItemShimmer
                        fetchedJSX={
                            <>
                                {currencySymbol}
                                {formatNumberWithCommas({
                                    num: roundOff(
                                        basePriceWOffersNMultiplier +
                                            basePriceWOffersNMultiplierCreatorTaxAmount +
                                            totalOrderBumpsValueWithTaxes +
                                            shippingValueWithTaxes
                                    ),
                                    locale: currencyLocale,
                                })}
                            </>
                        }
                        isFetched={isFetched}
                    />
                </div>
            </div>
            {representationalDiscount > 0 ? (
                <div className={styles.billItem}>
                    <div className={styles.billItemLabel}>Coupon discount</div>
                    <div
                        className={classNames(
                            styles.billItemvalue,
                            styles.discountItemValue
                        )}
                    >
                        <BillItemShimmer
                            fetchedJSX={
                                <>
                                    - {currencySymbol}
                                    {formatNumberWithCommas({
                                        num: roundOff(representationalDiscount),
                                        locale: currencyLocale,
                                    })}
                                </>
                            }
                            isFetched={isFetched}
                        />
                    </div>
                </div>
            ) : null}
            <div className={styles.billItem}>
                <div className={styles.billItemLabel}>
                    Internet Handling Fees
                </div>
                <div className={styles.billItemvalue}>
                    <BillItemShimmer
                        fetchedJSX={
                            <>
                                {currencySymbol}
                                {formatNumberWithCommas({
                                    num: roundOff(
                                        exlyIHFAmount + exlyTaxAmount
                                    ),
                                    locale: currencyLocale,
                                })}
                            </>
                        }
                        isFetched={isFetched}
                    />
                </div>
            </div>
            <hr className="m-0" />
            <div className={styles.billItem}>
                <div
                    className={classNames(
                        styles.billItemLabel,
                        styles.billItemBold
                    )}
                >
                    Total Amount
                </div>
                <div
                    className={classNames(
                        styles.billItemvalue,
                        styles.billItemBold
                    )}
                >
                    <BillItemShimmer
                        fetchedJSX={
                            <>
                                {currencySymbol}
                                {formatNumberWithCommas({
                                    num: roundOff(totalAmount),
                                    locale: currencyLocale,
                                })}
                            </>
                        }
                        isFetched={isFetched}
                    />
                </div>
            </div>
        </CheckoutAccordion>
    );
};

export default StandardPaymentSummary;
